import { SVG } from "@svgdotjs/svg.js";
import $ from "jquery";
import _ from "lodash";
import { Howl } from "howler";

const DEGREE_PITCH_MAP = {
  C: 1,
  D: 2,
  E: 3,
  F: 4,
  G: 5,
  A: 6,
  B: 7,
};

class NotImplementedError extends Error {
  constructor(message) {
    super(message);
    this.name = "NotImplementedError";
  }
}

class Follower {
  constructor(degrees) {
    this.degrees = degrees;
  }

  draw() {
    throw new NotImplementedError();
  }

  animate() {
    throw new NotImplementedError();
  }
}

class Player {
  constructor() {
    this.sounds = [];
    this.currentSoundIndex = -1;
    this.stepCallback = undefined;
    this.stopCallback = undefined;
  }

  play() {
    throw new NotImplementedError();
  }

  pause() {
    throw new NotImplementedError();
  }

  stop() {
    throw new NotImplementedError();
  }

  setCurrentSound(name) {
    const newIndex = _.findIndex(this.sounds, function (o) {
      return o.name === name;
    });
    this.currentSoundIndex = newIndex;
  }

  getCurrentSound() {
    return this.sounds[this.currentSoundIndex];
  }

  setStepCallback(callback) {
    this.stepCallback = callback;
  }

  setStopCallback(callback) {
    this.stopCallback = callback;
  }
}

export class Lesson1Player extends Player {
  constructor(sounds, score, keyboard, hand) {
    super();
    this.sounds = sounds.map(function (s) {
      const howl = new Howl({
        src: [s.url],
        onload: function () {
          console.log("audio loaded.");
        },
      });
      return { howl, ...s };
    });

    this.score = score;
    this.keyboard = keyboard;
    this.hand = hand;
    this.stepCallback = undefined;
  }

  play(endCallback) {
    const howl = this.getCurrentHowl();
    howl.play();
    howl.off("end");
    howl.once("end", () => {
      this.cleanup();
      if (this.stopCallback) {
        this.stopCallback();
      }
      endCallback();
    });
    this.stepCallback();

    const sound = this.getCurrentSound();
    const stepCallback = this.stepCallback;
    function step() {
      const seek = howl.seek();
      const secant = sound.tempo / 60;
      const currentPos = Math.ceil(seek * secant) - 5;

      stepCallback(currentPos);

      if (howl.playing() && seek < howl.duration()) {
        window.requestAnimationFrame(step);
      }
    }

    setTimeout(() => {
      window.requestAnimationFrame(step);
    }, 500);
  }

  pause() {
    this.getCurrentHowl().pause();
  }

  stop() {
    if (this.stopCallback) {
      this.stopCallback();
    }
    this.getCurrentHowl().stop();
    this.cleanup();
  }

  cleanup() {
    this.score.cleanup();
    this.keyboard.cleanup();
    this.hand.cleanup();
  }

  getCurrentHowl() {
    return this.getCurrentSound().howl;
  }
}

export class Lesson2Player extends Player {
  static TEMPO = 80;

  constructor() {
    super();
    this.placedCardMap = {
      1: null,
      2: null,
      3: null,
      4: null,
    };
  }

  play(endCallback) {
    for (let [pos, card] of Object.entries(this.placedCardMap)) {
      pos = parseInt(pos);
      const howl = card.howl;

      let waitSec = 0;
      if (pos > 1) {
        waitSec = (Lesson2Player.TEMPO / 60) * 1000 * (pos - 1);
      }

      setTimeout(() => {
        howl.play();
      }, waitSec);
      if (pos === 4) {
        howl.off("end");
        howl.once("end", () => {
          this.cleanup();
          endCallback();
        });
      }
    }

    const beatDoms = $(".lesson2-beat");
    const limit = (Lesson2Player.TEMPO / 60) * 1000 * 4;
    const span = limit / 8;
    let start;
    function step(timestamp) {
      if (start === undefined) start = timestamp;
      const elapsed = timestamp - start;

      const pos = Math.floor(elapsed / span);
      $(beatDoms).each((_, ele) => {
        const n = parseInt($(ele).data("n"));
        if (n === pos) {
          $(ele).css("background-color", "#3CB300");
        } else {
          $(ele).css("background-color", "#000");
        }
      });

      if (elapsed < limit) {
        window.requestAnimationFrame(step);
      }
    }

    setTimeout(() => {
      window.requestAnimationFrame(step);
    }, 200);
  }

  pause() {
    return;
  }

  stop() {
    return;
  }

  cleanup() {
    const beatDoms = $(".lesson2-beat");
    $(beatDoms).each((_, ele) => {
      $(ele).css("background-color", "#000");
    });
  }

  setCard(pos, card) {
    this.placedCardMap[pos] = card;
  }

  resetCard() {
    this.placedCardMap = {
      1: null,
      2: null,
      3: null,
      4: null,
    };
    $(".dropzone").empty();
  }
}

export class Lesson3Player extends Player {
  constructor(instruments, sounds) {
    super();

    this.instruments = instruments;

    this.soundMap = {};
    this.sounds = sounds.map((sound) => {
      const res = {};
      res["name"] = sound.name;
      res["tempo"] = sound.tempo;
      res["soundMap"] = {};

      for (const [instrument, urls] of Object.entries(sound.urlMap)) {
        res["soundMap"][instrument] = new Howl({
          src: urls,
          volume: 0.5,
          onload: function () {
            console.log("audio loaded.");
          },
        });
      }

      return res;
    });

    this.stepCallback = undefined;
  }

  play(endCallback) {
    const sound = this.getCurrentSound();
    for (const [, howl] of Object.entries(sound.soundMap)) {
      howl.play();
    }
    const howl = sound.soundMap["metronome"];
    howl.off("end");
    howl.once("end", () => {
      endCallback();
      if (this.stopCallback) {
        this.stopCallback();
      }
      this.cleanup();
    });

    const stepCallback = this.stepCallback;
    const startOffset = (60 / sound.tempo) * 4;
    const duration = howl.duration();
    const halfOfDuration = duration * 0.5;
    let passedHalfOfDuration = false;
    function step() {
      const pos = howl.seek();

      let scrollFlag = false;
      if (passedHalfOfDuration === false && pos >= halfOfDuration) {
        scrollFlag = true;
        passedHalfOfDuration = true;
      }

      stepCallback(pos, duration, startOffset, scrollFlag);

      if (howl.playing() && pos < duration) {
        window.requestAnimationFrame(step);
      }
    }

    setTimeout(() => {
      window.requestAnimationFrame(step);
    }, 500);
  }

  pause() {
    const sound = this.getCurrentSound();
    for (const [, howl] of Object.entries(sound.soundMap)) {
      howl.pause();
    }
  }

  stop() {
    if (this.stopCallback) {
      this.stopCallback();
    }
    this.cleanup();
  }

  updateVolume(volumeFlagMap) {
    let soleFlag = false;
    for (const [, flag] of Object.entries(volumeFlagMap.sole)) {
      if (flag) {
        soleFlag = true;
      }
    }

    if (soleFlag) {
      this.sounds.forEach((sound) => {
        for (const [instrument, flag] of Object.entries(volumeFlagMap.sole)) {
          if (flag) {
            sound["soundMap"][instrument].volume(0.5);
          } else {
            sound["soundMap"][instrument].volume(0);
          }
        }
      });
    } else {
      this.sounds.forEach((sound) => {
        for (const [instrument, flag] of Object.entries(volumeFlagMap.mute)) {
          if (flag) {
            sound["soundMap"][instrument].volume(0);
          } else {
            sound["soundMap"][instrument].volume(0.5);
          }
        }
      });
    }
  }

  cleanup() {
    this.sounds.forEach((sound) => {
      this.instruments.forEach((instrument) => {
        sound["soundMap"][instrument].stop();
      });
    });
  }
}

export class Lesson1Score extends Follower {
  static N_BAR = 4;
  static SCORE_TOP_PADDING = 50;
  static SCORE_SIDE_PADDING = 40;
  static heightSpace = 20;
  static STAFF_COLOR = "#808080";
  static NOTE_SIZE = [20, 20];
  static NOTE_COLOR = "#000";

  constructor(degrees) {
    super(degrees);
    this.parentDom = document.getElementById("lesson1-score");
    this.svg = null;
    this.svgWidth = this.parentDom.clientWidth;
    this.svgHeight = this.parentDom.clientHeight;
    this.cursor = null;
  }

  draw() {
    this.svg = SVG().addTo(this.parentDom).size(this.svgWidth, this.svgHeight);

    this.svg
      .image(`${process.env.PUBLIC_URL}/lesson1_score.png`)
      .size(this.svgWidth, this.parentDom.clientHeight);

    this.cursor = this.svg
      .rect(Lesson1Score.NOTE_SIZE[0], this.svgHeight)
      .fill({ color: "#3CB300", opacity: 0.2 })
      .move(999999, 0);
  }

  animate(currentPos) {
    const x = this.calcX(currentPos);
    this.cursor.move(x, 40);
  }

  cleanup() {
    if (this.cursor !== null) {
      this.cursor.move(999999, 0);
    }
  }

  calcX(i) {
    const j = Math.floor(i / 4);
    return (
      i * 32 +
      j * 12 +
      Lesson1Score.SCORE_SIDE_PADDING +
      Lesson1Score.NOTE_SIZE[0] / 2
    );
  }
}

export class Lesson1Keyboard extends Follower {
  static scale = 1.6;
  static whiteShape = [17 * Lesson1Keyboard.scale, 70 * Lesson1Keyboard.scale];
  static blackShape = [8 * Lesson1Keyboard.scale, 32 * Lesson1Keyboard.scale];

  constructor(degrees) {
    super(degrees);
    this.parentDom = document.getElementById("lesson1-keyboard");
    this.svg = null;
    this.svgWidth = Lesson1Keyboard.whiteShape[0] * 8;
    this.svgHeight = Lesson1Keyboard.whiteShape[1];
    this.whiteKeys = [];
  }

  draw() {
    this.svg = SVG().addTo(this.parentDom).size(this.svgWidth, this.svgHeight);

    let x = 0;
    for (let i = 0; i < 8; i++) {
      const key = this.svg
        .rect(Lesson1Keyboard.whiteShape[0], Lesson1Keyboard.whiteShape[1])
        .fill("#fff")
        .stroke({ color: Lesson1Score.STAFF_COLOR, width: 1 })
        .move(x, -1);
      x += Lesson1Keyboard.whiteShape[0];
      this.whiteKeys.push(key);
    }

    let counter = 0;
    for (let i = 0; i < 5; i++) {
      let x;
      if ((i + 1) % 3 === 0) {
        x =
          Lesson1Keyboard.whiteShape[0] * (counter + 1) +
          Lesson1Keyboard.whiteShape[0] -
          Math.ceil(Lesson1Keyboard.blackShape[0] / 2);
        counter += 2;
      } else {
        x =
          Lesson1Keyboard.whiteShape[0] * counter +
          Lesson1Keyboard.whiteShape[0] -
          Math.ceil(Lesson1Keyboard.blackShape[0] / 2);
        counter += 1;
      }

      this.svg
        .rect(Lesson1Keyboard.blackShape[0], Lesson1Keyboard.blackShape[1])
        .fill("#000")
        .stroke({ color: Lesson1Score.STAFF_COLOR, width: 1 })
        .move(x, -1);
    }
  }

  animate(currentPos) {
    if (this.degrees[currentPos]) {
      const degree = this.degrees[currentPos]["degree"];
      if (degree !== null) {
        const pitchPos = DEGREE_PITCH_MAP[degree] - 1;
        for (let i = 0; i < this.whiteKeys.length; i++) {
          const key = this.whiteKeys[i];
          if (i === pitchPos) {
            key.fill({ color: "#3CB300", opacity: 0.2 });
          } else {
            key.fill({ color: "#fff" });
          }
        }
      } else {
        this.whiteKeys.forEach((key) => {
          key.fill({ color: "#fff" });
        });
      }
    }
  }

  cleanup() {
    this.whiteKeys.forEach((key) => {
      key.fill({ color: "#fff" });
    });
  }
}

export class Lesson1Hand extends Follower {
  static defaultLeft = 10;

  constructor(degrees) {
    super(degrees);
    this.handImgDom = document.getElementById("lesson1-hand-img");
    this.prevPos = 0;
  }

  draw() {}

  animate(currentPos) {
    if (this.degrees[currentPos]) {
      const { handPos } = this.degrees[currentPos];
      if (handPos !== this.prevPos) {
        $(this.handImgDom)
          .delay(200)
          .animate(
            {
              left: Lesson1Hand.defaultLeft + handPos * 24,
            },
            200
          );
        this.prevPos = handPos;
      }
    }
  }

  cleanup() {
    $(this.handImgDom).animate({ left: Lesson1Hand.defaultLeft });
    this.prevPos = 0;
  }
}

export class Card {
  constructor(name, notes, audioSrc, imgSrc) {
    this.name = name;
    this.notes = notes;
    this.howl = new Howl({
      src: audioSrc,
      onload: function () {
        console.log("audio loaded.");
      },
    });
    this.audioSrc = audioSrc;
    this.imgSrc = imgSrc;
  }
}

export class SamplePlayer {
  constructor() {
    this.howl = null;
  }

  play(audioSrc) {
    if (this.howl && this.howl.playing()) {
      this.howl.stop();
    }
    this.howl = new Howl({
      src: audioSrc,
      onload: function () {
        console.log("audio loaded.");
      },
    });
    this.howl.play();
  }
}
